/**
 * Whether a connection on the Warehouses page works, and what a customer is told when it does not
 * (ACT2-1031).
 *
 * The page splits connections into working ones and an "Unhealthy" list. The customer sees that word
 * and one plain reason — never the internal state names behind it (paused, being enabled, opted in).
 * Its own file so the rule is testable without rendering a card.
 */
import type { RsAccountOverview } from './adopt/useAdoptAccounts';
import type { WarehouseAccount } from './useWarehouseAccounts';

/** What an unhealthy row offers besides Delete. */
export type UnhealthyRemedy = 'reconnect' | 'connect_account' | null;

export type ConnectionHealth = { healthy: true } | { healthy: false; reason: string; remedy: UnhealthyRemedy };

export const HEALTHY: ConnectionHealth = { healthy: true };

/** The RudderStack account a connection is bound to, read off the accounts overview, if any. */
export function boundRsAccount<T extends Pick<RsAccountOverview, 'connectionKey'>>(
  account: Pick<WarehouseAccount, 'connectionKey'>,
  overview: readonly T[],
): T | undefined {
  return overview.find((a) => a.connectionKey === account.connectionKey);
}

/**
 * Healthy, or the reason it is not.
 *
 * Only a `platform` connection can be unusable for want of a readable account: its credential lives
 * in RudderStack alone. A `local` connection bound to an account Lookout cannot read yet still signs
 * in with its own credential, so it stays healthy.
 */
export function connectionHealth(
  account: Pick<WarehouseAccount, 'pausedReason' | 'credentialSource' | 'rsAccountId'>,
  bound: Pick<RsAccountOverview, 'state'> | undefined,
): ConnectionHealth {
  if (account.pausedReason === 'account_deleted') {
    return {
      healthy: false,
      reason: 'The RudderStack account behind this connection was deleted.',
      remedy: 'reconnect',
    };
  }
  if (account.pausedReason === 'account_revoked') {
    return {
      healthy: false,
      reason: 'The RudderStack account behind this connection was revoked.',
      remedy: 'reconnect',
    };
  }
  if (account.credentialSource !== 'platform') return HEALTHY;
  if (!account.rsAccountId) {
    return {
      healthy: false,
      reason:
        'This connection isn’t linked to a warehouse account, so Lookout can’t sign in. Connect it to an account, or delete it.',
      remedy: 'connect_account',
    };
  }
  if (bound?.state === 'opted_in') {
    return {
      healthy: false,
      reason: 'Lookout can’t read this account’s credentials yet.',
      remedy: null,
    };
  }
  return HEALTHY;
}

/**
 * A person connected this connection to their own RudderStack account, so it is theirs to manage:
 * Lookout offers Detach, not Rotate or Delete. Only such a binding can be detached — the server
 * refuses Lookout-created ones (`lookout_owned`).
 */
export function ownedByRudderStack(bound: Pick<RsAccountOverview, 'adopted'> | undefined): boolean {
  return bound?.adopted === true;
}
